import { useCallback, useEffect, useRef, useState } from 'react';
import * as Device from 'expo-device';

/** iOS/Android simulators often never fire onCameraReady or have no camera hardware. */
const IS_SIMULATOR = !Device.isDevice;
/** Real devices: enable shutter if onCameraReady is slow; simulators: enable almost immediately. */
const READY_FALLBACK_MS = IS_SIMULATOR ? 400 : 3000;

export function useCameraShutterReady() {
  const [isCameraReady, setIsCameraReady] = useState(IS_SIMULATOR);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFallbackTimer = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    setIsCameraReady(IS_SIMULATOR);
    clearFallbackTimer();
    fallbackTimerRef.current = setTimeout(() => {
      fallbackTimerRef.current = null;
      setIsCameraReady(true);
    }, READY_FALLBACK_MS);
    return clearFallbackTimer;
  }, [clearFallbackTimer]);

  const onCameraReady = useCallback(() => {
    clearFallbackTimer();
    setIsCameraReady(true);
  }, [clearFallbackTimer]);

  const onMountError = useCallback(() => {
    if (IS_SIMULATOR) {
      setIsCameraReady(true);
      return;
    }
    setIsCameraReady(false);
  }, []);

  return { isCameraReady, onCameraReady, onMountError, isSimulator: IS_SIMULATOR };
}
